import { type Capture, parseCapture } from "./schema";

export interface CaptureDbRow {
  id: string;
  created_by: string;
  project_id: string;
  type: Capture["type"];
  title: string | null;
  recorded_at: string;
  duration_s: number;
  audio_path: string | null;
  keep_audio: boolean;
  status: Capture["status"];
  // Colonnes lourdes : optionnelles côté type car la liste (fetchCaptures) ne
  // les sélectionne plus (egress). Présentes pour les lectures mono-capture
  // (poll, fetchCaptureTranscript) et les écritures (mapCaptureToDbRow).
  transcript_raw?: string | null;
  transcript_segments?: Capture["transcriptSegments"] | null;
  summary?: string | null;
  // Phrase-cœur dénormalisée (légère) : tirée par la liste d'accueil.
  gist?: string | null;
  // Colonne générée en base : présence d'un transcript en 1 octet, sans texte.
  has_transcript?: boolean;
  // Dernière édition manuelle de la transcription (légère) : tirée par la
  // liste et la page dédiée pour signaler une analyse antérieure à l'édition.
  transcript_edited_at?: string | null;
  error_message: string | null;
  // Anti-perte : colonnes écrites seulement quand la capture les porte, et lues
  // seulement quand la requête les sélectionne (chemins existants inchangés).
  elapsed_s?: number | null;
  missing_audio_ms?: number | null;
  meeting_context: Capture["meetingContext"] | null;
  batch_extraction: boolean;
  approved_at: string | null;
  approved_by: string | null;
  created_at: string;
  updated_at: string;
}

export function mapCaptureToDbRow(capture: Capture): CaptureDbRow {
  return {
    id: capture.id,
    created_by: capture.createdBy,
    project_id: capture.projectId,
    type: capture.type,
    title: capture.title,
    recorded_at: capture.recordedAt,
    duration_s: capture.durationS,
    audio_path: capture.audioPath,
    keep_audio: capture.keepAudio,
    status: capture.status,
    transcript_raw: capture.transcriptRaw,
    transcript_segments: capture.transcriptSegments,
    summary: capture.summary,
    gist: capture.gist,
    error_message: capture.errorMessage,
    ...(capture.elapsedS !== undefined ? { elapsed_s: capture.elapsedS } : {}),
    ...(capture.missingAudioMs !== undefined ? { missing_audio_ms: capture.missingAudioMs } : {}),
    meeting_context: capture.meetingContext,
    batch_extraction: capture.batchExtraction,
    approved_at: capture.approvedAt,
    approved_by: capture.approvedBy,
    created_at: capture.createdAt,
    updated_at: capture.updatedAt,
  };
}

function normalizeIso(value: string): string {
  // Postgres timestamptz peut sortir "2026-05-13 23:37:53.222+00" (espace,
  // offset court) que Zod z.iso.datetime() rejette. On force le format ISO
  // strict via Date.
  return new Date(value).toISOString();
}

export function mapDbRowToCapture(row: CaptureDbRow): Capture {
  return parseCapture({
    id: row.id,
    createdBy: row.created_by,
    projectId: row.project_id,
    type: row.type,
    title: row.title,
    recordedAt: normalizeIso(row.recorded_at),
    durationS: row.duration_s,
    audioPath: row.audio_path,
    keepAudio: row.keep_audio,
    status: row.status,
    transcriptRaw: row.transcript_raw ?? null,
    transcriptSegments: row.transcript_segments ?? null,
    summary: row.summary ?? null,
    gist: row.gist ?? null,
    // N'émettre hasTranscript que si la colonne est présente dans la ligne :
    // préserve l'égalité stricte des lectures qui ne la sélectionnent pas, et
    // laisse le composant retomber sur la présence de transcriptRaw sinon.
    ...(row.has_transcript !== undefined ? { hasTranscript: row.has_transcript } : {}),
    ...(row.transcript_edited_at !== undefined
      ? {
          transcriptEditedAt: row.transcript_edited_at
            ? normalizeIso(row.transcript_edited_at)
            : null,
        }
      : {}),
    errorMessage: row.error_message,
    ...(row.elapsed_s !== undefined ? { elapsedS: row.elapsed_s } : {}),
    ...(row.missing_audio_ms !== undefined ? { missingAudioMs: row.missing_audio_ms } : {}),
    meetingContext: row.meeting_context,
    batchExtraction: row.batch_extraction ?? true,
    approvedAt: row.approved_at ? normalizeIso(row.approved_at) : null,
    approvedBy: row.approved_by,
    createdAt: normalizeIso(row.created_at),
    updatedAt: normalizeIso(row.updated_at),
  });
}
